import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import GetAppIcon from '@mui/icons-material/GetApp';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DeleteIcon from '@mui/icons-material/Delete';
import { QRCodeCanvas } from 'qrcode.react';
import { doc, updateDoc, collection, onSnapshot, deleteDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../index';
import { GameState, GameSession, Player, Submission, ScreenMode } from '../types';
import { useGameSession, useTeamStats, useLatestSubmissions } from '../hooks/useGameSession';
import { sanitizeDisplayName } from '../utils/normalize';
import { TEAM_COLORS } from '../constants/teamColors';
import { watchTeamScores } from '../utils/teamScore';

export const MasterDashboard: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [players, setPlayers] = useState<Player[]>([]);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  
  const { session } = useGameSession(sessionId || '');
  const { stats: teamStats } = useTeamStats(sessionId || '');
  const submissions = useLatestSubmissions(sessionId || '', 20);

  // プレイヤー一覧を監視
  useEffect(() => {
    if (!sessionId) return;

    const unsubscribe = onSnapshot(
      collection(db, 'battleRoyale', sessionId, 'players'),
      (snapshot) => {
        const playerList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Player));
        
        // スコア順でソート
        playerList.sort((a, b) => b.score - a.score);
        setPlayers(playerList);
      }
    );

    return () => unsubscribe();
  }, [sessionId]);

  // チームスコアのリアルタイム更新
  useEffect(() => {
    if (!sessionId) return;

    const unsubscribe = watchTeamScores(sessionId, (teamStats) => {
      // teamStatsはuseTeamStatsフックで自動的に更新される
      console.log('Team scores updated:', teamStats);
    });

    return () => unsubscribe();
  }, [sessionId]);

  const handleGameStateChange = async (newState: GameState) => {
    if (!sessionId) return;

    try {
      const updates: any = { state: newState };
      
      if (newState === GameState.PLAYING) {
        updates.startedAt = new Date();
      } else if (newState === GameState.ENDED) {
        updates.endedAt = new Date();
      }

      await updateDoc(doc(db, 'battleRoyale', sessionId), updates);
    } catch (err) {
      console.error('Error updating game state:', err);
    }
  };

  const handleDeletePlayer = async (playerId: string) => {
    if (!sessionId || !window.confirm('このプレイヤーを削除してもよろしいですか？')) return;

    try {
      // プレイヤーのドキュメントを削除
      await deleteDoc(doc(db, 'battleRoyale', sessionId, 'players', playerId));
      
      // プレイヤーの提出物も削除
      const submissionsQuery = query(
        collection(db, 'battleRoyale', sessionId, 'submissions'),
        where('playerId', '==', playerId)
      );
      const submissionsSnapshot = await getDocs(submissionsQuery);
      
      const deletePromises = submissionsSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);
      
      // チームスコアを更新（watchTeamScoresは自動更新のため、ここでは不要）
      // プレイヤーリストは自動的に更新される
    } catch (err) {
      console.error('Error deleting player:', err);
      alert('プレイヤーの削除に失敗しました');
    }
  };

  const exportData = (type: 'players' | 'submissions' | 'teams') => {
    let csvContent = '';
    let filename = '';

    switch (type) {
      case 'players':
        csvContent = 'ID,表示名,テーブル番号,チーム,スコア\n';
        csvContent += players.map(p => 
          `${p.id},${p.displayName},${p.tableNumber},${p.teamColor},${p.score}`
        ).join('\n');
        filename = 'players.csv';
        break;
        
      case 'submissions':
        csvContent = 'ID,プレイヤーID,答え,正解,ポイント,送信時刻\n';
        csvContent += submissions.map(s => 
          `${s.id},${s.playerId},${s.answerText},${s.isCorrect},${s.points},${s.submittedAt}`
        ).join('\n');
        filename = 'submissions.csv';
        break;
        
      case 'teams':
        csvContent = 'チーム,合計スコア,得点率\n';
        csvContent += teamStats.map(t => 
          `${t.teamColor},${t.totalScore},${t.scorePercentage}`
        ).join('\n');
        filename = 'team_totals.csv';
        break;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const getStateColor = (state: GameState) => {
    switch (state) {
      case GameState.WAITING: return 'default';
      case GameState.PLAYING: return 'success';
      case GameState.ENDED: return 'error';
    }
  };

  const getStateText = (state: GameState) => {
    switch (state) {
      case GameState.WAITING: return '待機中';
      case GameState.PLAYING: return '進行中';
      case GameState.ENDED: return '終了';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Grid container spacing={3}>
        {/* ヘッダー */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h4">
                バトロワなぞとき交渉会 - 管理画面
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Chip 
                  label={getStateText(session?.state || GameState.WAITING)}
                  color={getStateColor(session?.state || GameState.WAITING)}
                  sx={{ fontSize: '1.1rem', padding: '20px 16px' }}
                />
                {session?.state === GameState.WAITING && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => handleGameStateChange(GameState.PLAYING)}
                  >
                    ゲーム開始
                  </Button>
                )}
                {session?.state === GameState.PLAYING && (
                  <>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() => handleGameStateChange(GameState.WAITING)}
                    >
                      一時停止
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<StopIcon />}
                      onClick={() => handleGameStateChange(GameState.ENDED)}
                    >
                      ゲーム終了
                    </Button>
                  </>
                )}
                {session?.state === GameState.ENDED && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleGameStateChange(GameState.WAITING)}
                  >
                    待機状態に戻す
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* チーム統計 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              チーム別スコア
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>順位</TableCell>
                    <TableCell>チーム</TableCell>
                    <TableCell align="right">スコア</TableCell>
                    <TableCell align="right">得点率</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamStats.map((team, index) => (
                    <TableRow key={team.teamColor}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Chip 
                          label={`${TEAM_COLORS[team.teamColor]?.name || team.teamColor}チーム`}
                          size="small"
                          sx={{ 
                            bgcolor: TEAM_COLORS[team.teamColor]?.colorCode || team.teamColor,
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{team.totalScore}</TableCell>
                      <TableCell align="right">{team.scorePercentage.toFixed(2)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* 最新の正解 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400, overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              最新の回答
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {submissions.filter(s => s.isCorrect).slice(0, 10).map((sub) => {
                const player = players.find(p => p.id === sub.playerId);
                return (
                  <Box key={sub.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip 
                      label="正解" 
                      color="success" 
                      size="small"
                    />
                    <Typography variant="body2">
                      {sanitizeDisplayName(player?.displayName || '---')} - {sub.answerText}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        {/* タブコンテンツ */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
              <Tab label="プレイヤー一覧" />
              <Tab label="設定" />
              <Tab label="エクスポート" />
            </Tabs>

            {/* プレイヤー一覧 */}
            {tabValue === 0 && (
              <Box sx={{ mt: 3 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>順位</TableCell>
                        <TableCell>表示名</TableCell>
                        <TableCell>テーブル</TableCell>
                        <TableCell>チーム</TableCell>
                        <TableCell align="right">スコア</TableCell>
                        <TableCell align="center">操作</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {players.map((player, index) => (
                        <TableRow key={player.id}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{sanitizeDisplayName(player.displayName)}</TableCell>
                          <TableCell>{player.tableNumber}</TableCell>
                          <TableCell>
                            <Chip 
                              label={TEAM_COLORS[player.teamColor]?.name || player.teamColor}
                              size="small"
                              sx={{ 
                                bgcolor: TEAM_COLORS[player.teamColor]?.colorCode || player.teamColor,
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{player.score}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeletePlayer(player.id)}
                              title="プレイヤーを削除"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* 設定 */}
            {tabValue === 1 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  スクリーン表示設定
                </Typography>
                <FormControl component="fieldset" sx={{ mt: 2 }}>
                  <FormLabel component="legend">スクリーン表示モード</FormLabel>
                  <RadioGroup
                    value={session?.settings?.screenMode || ScreenMode.GROUP_6}
                    onChange={async (e) => {
                      if (!sessionId) return;
                      const newMode = e.target.value as ScreenMode;
                      
                      // screenModeに応じてshowTop3Onlyも更新
                      const updates: any = {
                        'settings.screenMode': newMode,
                        'settings.showTop3Only': newMode === ScreenMode.GROUP_3
                      };
                      
                      // maxRankToShowは不要（UnifiedScreenで設定するため）
                      
                      await updateDoc(doc(db, 'battleRoyale', sessionId), updates);
                    }}
                  >
                    <FormControlLabel 
                      value={ScreenMode.QR_CODE} 
                      control={<Radio />} 
                      label="QRコード（プレイヤー参加用）" 
                    />
                    <FormControlLabel 
                      value={ScreenMode.GROUP_6} 
                      control={<Radio />} 
                      label="グループランキング（1-6位）" 
                    />
                    <FormControlLabel 
                      value={ScreenMode.GROUP_4} 
                      control={<Radio />} 
                      label="グループランキング（1-4位）" 
                    />
                    <FormControlLabel 
                      value={ScreenMode.GROUP_3} 
                      control={<Radio />} 
                      label="グループランキング（1-3位）" 
                    />
                    <FormControlLabel 
                      value={ScreenMode.INDIVIDUAL} 
                      control={<Radio />} 
                      label="個人ランキング" 
                    />
                  </RadioGroup>
                </FormControl>
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/battle-royale/master/${sessionId}/settings`)}
                  >
                    イベント設定を開く
                  </Button>
                </Box>
              </Box>
            )}

            {/* エクスポート */}
            {tabValue === 2 && (
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={() => exportData('players')}
                >
                  players.csv
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={() => exportData('submissions')}
                >
                  submissions.csv
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={() => exportData('teams')}
                >
                  team_totals.csv
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* URLリンク */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              各種URL
            </Typography>
            
            {/* プレイヤー参加URL */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                プレイヤー参加URL（QRコードや短縮URLで共有）
              </Typography>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.100', 
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                wordBreak: 'break-all'
              }}>
                {window.location.origin}/battle-royale/player/{sessionId}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/battle-royale/player/${sessionId}`);
                    alert('URLをコピーしました');
                  }}
                >
                  URLをコピー
                </Button>
                <Button
                  size="small"
                  startIcon={<QrCodeIcon />}
                  onClick={() => setQrDialogOpen(true)}
                >
                  QRコード表示
                </Button>
              </Box>
            </Box>

            {/* スクリーン表示 */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              スクリーン表示
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => window.open(`/battle-royale/screen/${sessionId}`, '_blank')}
                sx={{ px: 4, py: 1.5 }}
              >
                スクリーンを開く
              </Button>
              <Typography variant="body2" color="text.secondary">
                現在のモード: {
                  session?.settings?.screenMode === ScreenMode.QR_CODE ? 'QRコード' :
                  session?.settings?.screenMode === ScreenMode.INDIVIDUAL ? '個人ランキング' :
                  session?.settings?.screenMode === ScreenMode.GROUP_3 ? 'グループ（1-3位）' :
                  session?.settings?.screenMode === ScreenMode.GROUP_4 ? 'グループ（1-4位）' :
                  'グループ（1-6位）'
                }
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* QRコードダイアログ */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)}>
        <DialogTitle>プレイヤー参加用QRコード</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', p: 3 }}>
          <QRCodeCanvas
            value={`${window.location.origin}/battle-royale/player/${sessionId}`}
            size={256}
            level="M"
            includeMargin={true}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            このQRコードをプロジェクターで表示するか、印刷して会場に掲示してください
          </Typography>
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => {
              const canvas = document.querySelector('canvas');
              if (canvas) {
                const url = canvas.toDataURL('image/png');
                const link = document.createElement('a');
                link.download = `battle-royale-${sessionId}-qr.png`;
                link.href = url;
                link.click();
              }
            }}
          >
            QRコードをダウンロード
          </Button>
        </DialogContent>
      </Dialog>
    </Container>
  );
};